import {Linking, Platform} from 'react-native';
import Toast from 'react-native-toast-message';
import {isAxiosError} from 'axios';
import {toJSON} from 'flatted';
import {configureLogger} from 'mobx-log';

import config from '../config';
import rnuc from '../config.generated';

// Disable mobx-log if LOGGING_ENABLED is false
if (rnuc.LOGGING_ENABLED === false || rnuc.LOGGING_ENABLED === 'false') {
  configureLogger({
    filters: {
      computeds: false,
      actions: false,
      observables: false,
    },
  });
}
// Deliberately NOT the `queries/promotions` barrel: the barrel re-exports
// `usePromotions`, which imports `stores/use-stores`, which imports this file —
// a cycle `npm run check:circular` fails on.
import {
  promotionsEnabled,
  promotionsQueryOptions,
} from '../queries/promotions/promotionsQueryOptions';
import {queryClient} from '../queries/queryClient';
import {crashlyticsService} from '../services/CrashlyticsService';
import {sentryService} from '../services/SentryService';
import apiService from '../transport/apiService';
import {registerInterceptorCallbacks} from '../transport/interceptorCallbacks';
import {logError} from '../utils/errorLogging';
import {logger} from '../utils/logger';
import {type SessionEvent, subscribeSessionEvents} from '../utils/sessionSync';
import {getUserAgentInfo} from '../utils/userAgent';
import {isUpgradeRequired} from '../utils/versionCheck';

import {BrandsStore} from './brandsStore';
import {CartStore} from './cartStore';
import {FeedStore} from './feedStore';
import {FilterStore} from './filterStore';
import {GlobalStore} from './globalStore';
import {LinesParams, LinesStore} from './linesStore';
import {LoyaltyInformation} from './loyaltyInformationStore';
import {OffersStore} from './offersStore';
import {RangeStore} from './rangeStore';
import {RewardsStore} from './rewardsStore';
import {SettingsStore} from './settingsStore';
import {ShopSelectionStore} from './shopSelectionStore';
import {UserStore} from './userStore';

const getInitialURL = async () => {
  const initialURL = await Linking.getInitialURL();
  return initialURL ? new URL(initialURL) : null;
};

const getUrlSegments = (url: URL) => url.pathname.split('/').filter(segment => segment !== '');

const isFirstUrlSegment = (urlStr: URL, segment = '') => getUrlSegments(urlStr)[0] === segment;

const isRootURL = async () => {
  const url = await getInitialURL();
  if (!url && Platform.OS !== 'web') return true;

  return url && url.pathname === '/';
};

export const isAccountURL = async () => {
  const url = await getInitialURL();
  if (!url && Platform.OS !== 'web') return false;

  return url && url.pathname === '/password';
};

export const isInformationUrl = async () => {
  const url = await getInitialURL();
  if (!url && Platform.OS !== 'web') return false;
  return url && isFirstUrlSegment(url, 'information');
};

const isGiftCardUrl = async () => {
  const url = await getInitialURL();
  return url && isFirstUrlSegment(url, 'gift-cards');
};

const isStoreSwitchUrl = async () => {
  const url = await getInitialURL();
  if (!url) return false;

  const pathSegments = getUrlSegments(url);
  return pathSegments.length === 2 && pathSegments[0] === 'store';
};

const getStoreSwitchUrlSalesforceId = async () => {
  const url = await getInitialURL();
  if (!url) return null;

  const pathSegments = getUrlSegments(url);
  return pathSegments[1];
};

const GIFT_CARD_STORE = config.GIFT_CARD_STORE;

export class RootStore {
  brandsStore: BrandsStore;
  cartStore: CartStore;
  feedStore: FeedStore;
  filterStore: FilterStore;
  globalStore: GlobalStore;
  offersStore: OffersStore;
  rangeStore: RangeStore;
  shopSelectionStore: ShopSelectionStore;
  userStore: UserStore;
  rewardsStore: RewardsStore;
  loyaltyInformationStore: LoyaltyInformation;
  linesStore: Record<string, LinesStore> = {};
  settingsStore: SettingsStore;

  constructor() {
    this.brandsStore = new BrandsStore(this);
    this.cartStore = new CartStore(this);
    this.feedStore = new FeedStore(this);
    this.filterStore = new FilterStore(this);
    this.globalStore = new GlobalStore(this);
    this.offersStore = new OffersStore(this);
    this.rangeStore = new RangeStore(this);
    this.shopSelectionStore = new ShopSelectionStore(this);
    this.userStore = new UserStore(this);
    this.rewardsStore = new RewardsStore(this);
    this.loyaltyInformationStore = new LoyaltyInformation(this);
    this.settingsStore = new SettingsStore(this);

    // Register interceptor callbacks AFTER stores are initialized
    // This breaks the circular dependency between transport and stores
    registerInterceptorCallbacks({
      getDeviceId: () => this.globalStore?.deviceId || null,
      getServerSessionId: () => this.userStore?.serverSessionId || null,
      isUserLoggedIn: () => this.userStore?.isLoggedIn || false,
      logoutUser: () => this.userStore?.logout(),
      storeServerSessionId: (id: string) => this.userStore?.storeServerSessionId(id),
    });

    // Cross-tab session sync (web only — native builds resolve the no-op twin).
    subscribeSessionEvents(event => {
      void this.handleCrossTabSessionEvent(event);
    });

    // Safety net for anything BroadcastChannel can't catch (tab opened before
    // the event, logout from another device, server-side invalidation).
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          void this.revalidateSessionOnFocus();
        }
      });
    }
  }

  resetOffersStore = () => {
    this.offersStore = new OffersStore(this);
  };

  public getLinesStore = (params: LinesParams) => {
    const key = toJSON(params);

    if (!this.linesStore[key]) {
      this.linesStore[key] = new LinesStore(params);
    }

    return this.linesStore[key];
  };

  /**
   * Session-scoped product lines live in MobX `LinesStore` singletons, not TanStack.
   * Replacing the map alone leaves mounted carousels holding the old instance with
   * stale `sell_price_cents`. Invalidate in place, drop the map, then reload any
   * still-held instances under the current session.
   */
  invalidateLinesStores = () => {
    const live = Object.values(this.linesStore);
    for (const store of live) {
      store.invalidate();
    }
    this.linesStore = {};
    for (const store of live) {
      void store.reload();
    }
  };

  private getShopSalesforceId = async () => {
    const isGiftCardStoreURL = await isGiftCardUrl();

    let localStorageStore = await this.shopSelectionStore.loadShopSalesforceIdFromLocalStorage();

    // Fix for store switch when there's no store selected
    const isStoreSwitch = await isStoreSwitchUrl();
    if (!localStorageStore && isStoreSwitch) {
      const storeSwitchSalesforceId = await getStoreSwitchUrlSalesforceId();
      if (storeSwitchSalesforceId) {
        await this.shopSelectionStore.setShopSalesforceIdToStorage(storeSwitchSalesforceId);
      }
      localStorageStore = storeSwitchSalesforceId;
    }

    // TODO: A lot of the existing mobX code depends on the "current selected
    // store" loaded from the local storage. This tries to reuse that logic
    // without having to refactor all that right now. For this, we play with
    // a new  `LIQUOR_SHOP_SALESFORCE_STORAGE_KEY` that keeps the "previous
    // selected/preferred liquor store"
    let shopSalesforceId = null;
    if (isGiftCardStoreURL) {
      // If the user is visiting the Gift Card Store (/gift-cards), use the
      // fixed store "Salesforce ID" (number/id) for the Gift Card Store
      // (and also save the "preferred liquor store" to local storage if the
      // selected store was a liquor store)
      if (localStorageStore !== GIFT_CARD_STORE) {
        this.shopSelectionStore.setLiquorShopSalesforceIdToStorage(localStorageStore);
      }
      shopSalesforceId = GIFT_CARD_STORE;
      this.shopSelectionStore.setShopSalesforceIdToStorage(shopSalesforceId);
    } else {
      // If the user is visiting the regular liquor store and the selected
      // store in local storage was the "Gift Card" store, check if there's a
      // selected ("preferred") liquor store in local storage and use that
      if (localStorageStore === GIFT_CARD_STORE) {
        let localStorageLiquorStore =
          await this.shopSelectionStore.loadLiquorShopSalesforceIdFromLocalStorage();

        // TODO: hack for old buggy dev versions
        if (localStorageLiquorStore === config.GIFT_CARD_STORE) {
          localStorageLiquorStore = null;
          await this.shopSelectionStore.clearLiquorShopSalesforceIdFromStorage();
        }

        if (localStorageLiquorStore) {
          await this.shopSelectionStore.setShopSalesforceIdToStorage(localStorageLiquorStore);
        }
        shopSalesforceId = localStorageLiquorStore;
      } else {
        shopSalesforceId = localStorageStore;
      }
    }
    return shopSalesforceId;
  };

  /**
   * Check if the app version meets minimum requirements from server config
   * @returns true if version is acceptable, false if upgrade is required
   */
  checkAppVersion = async (): Promise<{passed: boolean; sentryEnabled: boolean}> => {
    try {
      // Fetch server config (fail open on error)
      const clientConfig = await apiService.getClientConfig();
      if (!clientConfig) {
        // API call failed - allow app to continue (fail open)
        logger.warn('RootStore', 'Unable to fetch client config, skipping version check');
        return {passed: true, sentryEnabled: false};
      }

      const {minimum_frontend_version} = clientConfig;
      const sentryEnabled = clientConfig.sentry_enabled ?? false;

      // Get current app version
      const userAgentInfo = await getUserAgentInfo();
      const currentVersion = userAgentInfo.appVersion;

      logger.debug('RootStore', 'Version check', {
        currentVersion,
        minimumVersion: minimum_frontend_version,
      });

      // Check if upgrade is required
      if (isUpgradeRequired(currentVersion, minimum_frontend_version)) {
        logger.info('RootStore', 'App upgrade required', {
          currentVersion,
          minimumVersion: minimum_frontend_version,
        });
        this.globalStore.setRequiresUpgrade(true, currentVersion, minimum_frontend_version);
        return {passed: false, sentryEnabled: false};
      }

      return {passed: true, sentryEnabled};
    } catch (error) {
      // Any unexpected error - fail open to avoid blocking users
      logger.warn('RootStore', 'Version check failed unexpectedly', {error});
      return {passed: true, sentryEnabled: false};
    }
  };

  // Note: This is a refactoring of a lot of mobX autorun code that was
  // distributed across the stores and was  hard to follow and maintain.
  // The referenced methods are still unchanged.
  // (Use reactions sparingly!
  // https://mobx.js.org/reactions.html#use-reactions-sparingly)
  initialiseApp = async () => {
    // Initialize Crashlytics first so it can catch errors during app init
    await crashlyticsService.initialize(this);

    this.globalStore.setIsAppInitialising(true);
    try {
      // 0. Check app version requirements
      //    If app version is below minimum required, block further initialization
      const {passed: versionCheckPassed, sentryEnabled} = await this.checkAppVersion();

      // Initialize Sentry for web (after config fetch)
      await sentryService.initialize(this, sentryEnabled);

      if (!versionCheckPassed) {
        // Version check failed - upgrade required
        // App will show ForceUpgradeScreen via globalStore.requiresUpgrade
        return;
      }

      // 1. Load the device ID from local storage or generate a new one
      //    Sets `this.globalStore?.deviceId`
      await this.globalStore.loadDeviceIdFromLocalStoreOrGenerateNew();

      // 2. Load the previously selected store "Salesforce ID" from local storage
      //    or check if the user is visiting the "Gift Cards Store" (/gift-cards)

      let shopSalesforceId = null;

      /**
       * Only for web
       */
      const storeId =
        Platform.OS === 'web' && new URLSearchParams(window.location.search).get('storeId');

      if (storeId) {
        shopSalesforceId = storeId;
        // await this.shopSelectionStore.setShopSalesforceIdToStorage(storeId);
        await this.shopSelectionStore.findSalesforceShop(storeId);
      } else {
        shopSalesforceId = await this.getShopSalesforceId();
      }

      const isAccountRoute = await isAccountURL();
      if (isAccountRoute) {
        this.globalStore.setIsAccountRoute(true);
      }

      const isInformationRoute = await isInformationUrl();
      if (isInformationRoute) {
        this.globalStore.setIsInformationRoute(true);
      }

      if (!shopSalesforceId) {
        // 2.1. If a store was not selected, finish the loading process (the
        //      "Authentication flow" in App.tsx will show the store selection flow)
        logger.info('RootStore', 'No shop selected on app start, showing store selector');
        this.globalStore.setWasStoreSelectedOnAppStart(false);
        this.loadDefaultStoreContent();
        return;
      }

      // 3. Restore server session (DIS-1026: cookie check on web, EncryptedStorage on native)
      await this.userStore.restoreSession();

      // 4. Load the store data (feed, promotions, brands, segments, etc)
      await this.shopSelectionStore.retrieveShopData();
      const isRoot = await isRootURL();
      if (isRoot) {
        // This is the "traditional" app start. There are too many initial requests and waiting
        // for all of them takes too long. More refactoring is still needed.
        this.initialiseSelectedStoreData();
      } else {
        // A lot of the "deep links" require the store data to be loaded. We are currently willing
        // to pay the price of waiting in these cases
        await this.initialiseSelectedStoreData();
      }

      if (this.userStore.isLoggedIn) {
        await this.initialiseUserData();
      }
    } catch (err) {
      logError(err, 'RootStore.initialise');
    } finally {
      // Always ensure we finish initializing, even if there were errors
      // This prevents the app from being stuck on the splash screen
      this.globalStore.setIsAppInitialising(false);
    }
  };

  loadDefaultStoreContent = () => {
    // DIS-1201: feedStore.loadFeedList removed — DiscoverListScreen / FeedList
    // fire it on mount when their respective lists are empty.
    this.feedStore.getSegments(config.DEFAULT_STORE, config.DEFAULT_STORE_STATE);
    // DIS-1567: there is deliberately NO promotions prefetch here. Do not
    // "restore" one.
    //
    // This method runs only on the no-store-selected branch (see the
    // `!shopSalesforceId` early return in `initialise`). Under the MobX store,
    // `getPromotions(DEFAULT_STORE, …)` populated a single global object that
    // every consumer read, so seeding it with the default store worked. The
    // query cache is keyed per store: every consumer builds its key from
    // `shopSelectionStore.selectedStore`, which is null on this branch, so they
    // all address `['promotions','list',null,null]` while a prefetch here would
    // write `['promotions','list',DEFAULT_STORE,DEFAULT_STORE_STATE]`. No reader
    // can reach that entry, and `persistConfig` would dehydrate it to disk for
    // 24h. `feedStore.getSegments` above is unaffected — it still writes the one
    // global MobX object it always did.
    //
    // Promotions for the selected store are prefetched by
    // `initialiseSelectedStoreLoyaltyDependentData` once a store exists, and
    // every consumer's own `usePromotions` fetches on mount.
  };

  initialiseUserSession = async () => {
    // Sets `this.globalStore?.deviceId`
    await this.globalStore.loadDeviceIdFromLocalStoreOrGenerateNew();

    // DIS-1026: restore server session (cookie check on web, EncryptedStorage on native)
    await this.userStore.restoreSession();
  };

  initialiseUserDataNonBlocking = async (options?: {zohoId?: string}) => {
    this.loyaltyInformationStore.reset();
    await this.userStore.retrieveUserInfoV2({retryable: true, zohoId: options?.zohoId});
    this.cartStore.fullyReloadOrderFromMFL();
    // Payment card is now fetched via usePaymentCard hook (TanStack Query)
    this.rewardsStore.loadRewardsForUser(this.userStore.phoneNumber);

    // Payment services and wishlist are now managed by TanStack Query - auto-fetches when hooks are used
  };

  login = async () => {
    // Drop session-scoped product line caches so carousels / Members Price list
    // re-fetch under the new shopper (mirrors logoutUser).
    this.invalidateLinesStores();
    await this.initialiseUserData();
    await this.initialiseSelectedStoreLoyaltyDependentData();
  };

  initialiseUserData = async () => {
    this.globalStore.setIsUserDataInitialising(true);
    try {
      await this.initialiseUserDataNonBlocking();
      this.globalStore.setIsUserDataInitialising(false);
    } catch (err) {
      this.globalStore.setIsUserDataInitialising(false);
      logError(err, 'RootStore.initialiseUserData');
    }
  };

  initialiseSelectedStoreData = async () => {
    try {
      const selectedStore = this.shopSelectionStore?.selectedStore;
      if (!selectedStore?.id) {
        logger.warn(
          'RootStore',
          'No store selected, skipping store-dependent data initialization. User will need to select a store.',
        );
        this.globalStore.setIsSwitchingStore(false);
        return; // Gracefully skip instead of throwing
      }

      // DIS-1201: feedStore.loadFeedList removed from cold-start waterfall.
      // DiscoverListScreen / FeedList fire it on mount when lists are empty.
      // Cold-start LCP improvement: the awaited /content/feed Airtable call
      // (~800ms) no longer blocks subsequent store-data init.
      await this.initialiseSelectedStoreLoyaltyDependentData();
      await this.brandsStore.loadBrands(); // no dependencies
      await this.feedStore.getSegments(); // needs shop id
      await this.rangeStore.loadDepartments();
      this.filterStore.loadSortOptions();
      this.filterStore.loadFilterOptions();
      this.shopSelectionStore.loadLiquorLicense();
      this.cartStore.fullyReloadOrderFromMFL();

      // Payment services and wishlist are now managed by TanStack Query - auto-fetches when hooks are used
      if (selectedStore?.store_number === config.GIFT_CARD_STORE) {
        await this.rangeStore.loadRangeLines();
      }
    } catch (error) {
      logError(error, 'RootStore.initialiseSelectedStoreData');
      this.globalStore.setIsSwitchingStore(false);
    }
  };

  initialiseSelectedStoreLoyaltyDependentData = async () => {
    const selectedStore = this.shopSelectionStore.selectedStore;
    // DIS-1567: `prefetchQuery` delegates to `fetchQuery`, which ignores the
    // `enabled` option entirely (`enabled` is a QueryObserver concept —
    // verified against @tanstack/query-core 5.100.9). So the storeNumber/gift-card
    // guard that `promotionsStore.getPromotions` applied internally has to be
    // applied here instead: `login()` and `logoutUser()` both call this method
    // without the `selectedStore?.id` check `initialiseSelectedStoreData` does,
    // and an undefined or gift-card storeNumber would otherwise reach the
    // middleware and 400.
    //
    // Written as a wrap rather than an early return on purpose: this method's
    // name invites more loyalty-dependent init steps being appended below, and
    // an early return here would silently skip every one of them whenever no
    // store is selected. `promotionsEnabled` is the same predicate shared with
    // the query options, `usePromotions` and `ensurePromotions`, so this call
    // site cannot drift from them. The guard covers the prefetch and only the
    // prefetch.
    if (promotionsEnabled(selectedStore?.store_number)) {
      await queryClient.prefetchQuery(
        promotionsQueryOptions(
          selectedStore?.store_number,
          selectedStore?.address?.administrative_area,
        ),
      );
    }
  };

  /** React to a session event broadcast by ANOTHER tab. */
  handleCrossTabSessionEvent = async (event: SessionEvent) => {
    if (event.type === 'logout') {
      if (!this.userStore.isLoggedIn) return; // already logged out — nothing to do
      await this.passiveLogout();
      return;
    }
    // 'login'
    if (this.userStore.isLoggedIn) return; // already logged in — nothing to do
    const restored = await this.userStore.restoreSession();
    if (restored) {
      await this.login();
    }
  };

  /**
   * Logout because the session died elsewhere (another tab logged out, or the
   * visibility probe found the server session gone). Local cleanup only: no
   * POST /ecommerce/logout (already dead server-side), and latch first so
   * in-flight 401s stay quiet (no second toast).
   */
  passiveLogout = async (options?: {broadcast?: boolean}) => {
    apiService.latchUnauthorizedGuard();
    Toast.show({
      type: 'errorMessage',
      text1: 'You have been logged out.',
      visibilityTime: 4000,
    });
    await this.userStore.logout({callApi: false, broadcast: options?.broadcast ?? false});
  };

  private lastSessionProbeAt = 0;
  private static readonly SESSION_PROBE_DEBOUNCE_MS = 60_000;

  /**
   * On tab focus, cheaply confirm the server session still exists
   * (GET /ecommerce/session — Redis-only, no MFL roundtrip).
   *
   * CRITICAL: only a definitive 401 means "logged out". Network errors,
   * timeouts and 5xx mean "can't tell right now" and must do NOTHING —
   * logging a user out because their wifi blipped is exactly the bug this
   * project exists to prevent. Two middleware 401 shapes are definitive:
   *  - sessionV2Middleware: {code, recovery: 'reauth'} (stale cookie, Redis miss)
   *  - getSession controller: {valid: false} (no usable session credential at all,
   *    e.g. the cookie was already cleared by a logout elsewhere)
   */
  revalidateSessionOnFocus = async () => {
    if (!this.userStore.isLoggedIn) return;
    const now = Date.now();
    if (now - this.lastSessionProbeAt < RootStore.SESSION_PROBE_DEBOUNCE_MS) return;
    this.lastSessionProbeAt = now;

    try {
      await apiService.checkSession(); // already uses skipUnauthorizedHandler
    } catch (error) {
      const status = isAxiosError(error) ? error.response?.status : undefined;
      const body = isAxiosError(error)
        ? (error.response?.data as {recovery?: string; valid?: boolean} | undefined)
        : undefined;
      if (status === 401 && (body?.recovery === 'reauth' || body?.valid === false)) {
        // Session is definitively dead — also broadcast so sibling tabs sync now.
        await this.passiveLogout({broadcast: true});
      }
      // Anything else (offline, 5xx, timeout): fail open, keep the session.
    }
  };

  logoutUser = async () => {
    // DIS-1330: drop the store-scoped competition header so a different account
    // signing in within the same web session (one JS process) isn't gated by the
    // previous user's store. Mirrors the queryClient.clear() data-leak guard below.
    apiService.setStoreNumber(null);
    // Drop session-scoped product line caches (carousels / Members Price). Query
    // clear does not cover these MobX singletons — they seed nav with stale
    // sell_price_cents after logout. Invalidate live instances, don't only `{}`.
    this.invalidateLinesStores();
    try {
      await this.cartStore.fullyReloadOrderFromMFL();
      // Clear all TanStack Query cache on logout to prevent data leakage between accounts
      queryClient.clear();
      // DIS-1567: MUST stay after the clear above. This call now prefetches
      // promotions INTO the query cache; clearing afterwards would discard that
      // fetch and leave promotions empty until a consumer remounted.
      await this.initialiseSelectedStoreLoyaltyDependentData();
      await this.rewardsStore.logout();
      this.loyaltyInformationStore.reset();
    } catch (err) {
      logError(err, 'RootStore.logout');
    }
  };
}

export const rootStore = new RootStore();
